import { NgIf } from '@angular/common';
import {
    AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, OnChanges, OnDestroy,
    OnInit, SimpleChanges, ViewChild,
    computed,
    signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ICompany, RequestLicense, RequestUserAccessEmailSet, RequestUserState } from '@myapp/features/admin/models';
import { AdminService } from '@myapp/features/admin/services';
import { AuthPermIdentifier, RequestUserPermission, ResponseUserPermission } from '@myapp/modules/fwcore/models';
import {
    AuthorizationService, ConfirmationDialogHtmlPaneService, ConfirmationDialogService,
    NotificationDialogService, NotificationPopupService
} from '@myapp/modules/fwcore/services';
import { ControlResizerOptions, ControlResizerService, IsgHelperService } from '@mysrc/services/framework';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { SelectEvent, TabStripModule } from '@progress/kendo-angular-layout';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TriStateCheckBoxComponent, ClickFilterComponent } from '../../../widgets';
import { UserImportComponent } from '../../components/users/user-import/user-import.component';
import { UserStateComponent } from '../../components/users/user-state/user-state.component';
import { IsgLoaderComponent } from '../../../../modules/fwcore/components/isg-loader/isg-loader.component';

@Component({
  selector: 'app-page-user-license',
  standalone: true,
  imports: [RouterLink, TabStripModule, TextBoxModule, FormsModule, TriStateCheckBoxComponent, ClickFilterComponent, ButtonModule
    , UserStateComponent, NgIf, UserImportComponent, IsgLoaderComponent],
  templateUrl: './page-user-license.component.html',
  styleUrls: ['./page-user-license.component.scss']
})
export class PageUserLicenseComponent implements AfterViewChecked, OnInit, OnDestroy, AfterViewInit, OnChanges {

  //------------------------------ Constants -----------------------------

  /** Permission code allowing user import. */
  protected readonly ctCodeUserImport = 'user-import';

  /** Title used for the user download dialogs. */
  protected readonly ctDownloadDialogTitle = 'Download Users';

  /** File extension for the user download. */
  protected readonly ctDownloadExtension = '.xlsx';

  /*-------------------------- Input / Outputs ------------------------*/


  /*-------------------------- public properties / methods ------------*/

  public currentLicenseId: string | null = null;

  public currentCompanyId: string | null = null;

  public userStartsWith: string = "";

  public userAssigned: boolean | null = true;

  public userActive: boolean | null = true;

  public companyName: string | undefined | null = null;

  /*-------------------------- Internal properties  -------------------*/

  protected companies: ICompany[] | undefined;

  protected destroy$ = new Subject();

  protected height!: number;

  protected viewInit: boolean = false;

  protected loading: boolean = false;

  /** Controls visibility of the full-page loading overlay. */
  protected loadingPanelVisible = signal<boolean>(false);


  protected hrefLicenses: string = "./admin/license";
  protected hrefUserManagement: string = "./admin/user/license";
  protected hrefLicenseRoles: string = "./admin/role";
  protected hrefUserPermission: string = "./admin/user/permission";

  @ViewChild(UserStateComponent)
  protected componentUserState!: UserStateComponent;

  @ViewChild(UserImportComponent)
  protected componentUserImport!: UserImportComponent;

  //-------------------- Permission Properties -------------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {

  }

  /** Permissions returned for the current user/route. */
  protected perms?: AuthPermIdentifier[] = [];

  /** Permission codes resolved for the current user/route. */
  protected permCodes = signal<Set<string>>(new Set<string>());

  /** True once the user-permissions request has completed. */
  protected permsLoaded = false;

  /** True when the user-import tab is available. */
  protected hasUserImport = computed<boolean>(() => this.permCodes().has(this.ctCodeUserImport));

  /*--------------------------- Constructor  --------------------------*/

  constructor(protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected dialogConfirmPane: ConfirmationDialogHtmlPaneService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected controlResizer: ControlResizerService
    , protected cdr: ChangeDetectorRef
    , protected hlp: IsgHelperService
  ) {

  }

  /*--------------------------- ng  -----------------------------------*/

  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {
          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) { this.router.navigate(['./admin/license']); }

          this.updateCompanyInfo();

          this.hrefLicenses = `./admin/license?id=` + this.currentLicenseId;
          this.hrefUserManagement = "./admin/user/license?id=" + this.currentLicenseId;
          this.hrefLicenseRoles = `./admin/role?id=` + this.currentLicenseId;
          this.hrefUserPermission = `./admin/user/permission?id=` + this.currentLicenseId;
        }
      );

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );

    this.reqPerm.hierarchyCodes = ['users'];

    this.loadUserPermissions(this.reqPerm);

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewChecked(): void {

    this.sizeChange();
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {

  }

  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {


  }


  /** Fetches user permissions for the current route and applies derived flags. */
  protected loadUserPermissions(reqPerm: RequestUserPermission): void {

    this.serviceAuth.getData$<RequestUserPermission, ResponseUserPermission>(reqPerm, this.serviceAuth.endpointUserPermission)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {

        this.perms = resp.data ?? [];

        this.permCodes.set(new Set(this.perms.map(p => p.code).filter((c): c is string => !!c)));

        this.permsLoaded = true;
      });
  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.sizeChange();
  }

  onTabSelect(e: SelectEvent): void {
    this.sizeChange();
  }

  onSendEmails(e: Event): void {

    var userIds = this.componentUserState.checkedIds;

    if (!userIds || userIds.length < 1) {
      this.dialogNotification.openNotificationDialog("Send Access Emails", "Select users to send access emails to by ticking their checkboxes");
      return;
    }

    var msg = "Do you wish to proceed and send out access emails to users?";

    this.dialogConfirmPane.open('Send Access Emails', msg, 300)
      .subscribe((result: any) => {
        if (result.value) {

          const req: RequestUserAccessEmailSet = {
            licenseId: this.currentLicenseId
            , data: userIds
          };

          this.sendAccessEmails(req);
        }
      });
  }

  /**
 * Downloads every user for the current license as an Excel workbook.
 * Ignores the on-screen filters; the server enforces company access.
 */
  protected onDownloadUsers(e: Event): void {

    if (!this.currentLicenseId) {
      this.dialogNotification.openNotificationDialog(
        this.ctDownloadDialogTitle,
        'No license is currently selected'
      );
      return;
    }

    const req: RequestUserState = {
      licenseId: this.currentLicenseId
      , companyId: this.currentCompanyId
    };

    this.downloadUsers(req);
  }

  /*--------------------------- internal functions  -------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 94;
      options.offsetBottomHeight = 21;

      this.height = this.controlResizer.calculateHeight(options);

      options.offsetTopHeight = 6;
      options.offsetBottomHeight = 29;
      this.sizeChangeSiblings(options);
    }

  }

  protected sizeChangeSiblings(options: ControlResizerOptions) {

    if (this.componentUserState)
      this.componentUserState.heightChange(options);

    if (this.componentUserImport)
      this.componentUserImport.heightChange(options);

  }

  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {
        if (resp.data && resp.data.length === 1) {
          this.companyName = resp.data[0].companyName;
          this.currentCompanyId = (resp.data[0].companyId) ? resp.data[0].companyId : null;
        }
        else {
          this.companyName = "";
          this.currentCompanyId = null;
        }
      }
    );
  }

  protected sendAccessEmails(req: RequestUserAccessEmailSet) {

    this.loading = true;

    this.adminService.setUserAccessEmail$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success) {
          let msg = `${res.emailTotal} processed ${res.emailSuccess} sent, ${res.emailFailure} failure(s). ${res.message}`;
          this.dialogNotification.openNotificationDialog('Send Access Emails', msg);
        }
        else {
          let msg = `${res.emailTotal} processed ${res.emailSuccess} sent, ${res.emailFailure} failure(s).\n ${res.message}`;
          this.dialogNotification.openNotificationDialog('Send Access Emails', msg);
        }
      });

  }

  /** Shows the full-page loading overlay. */
  protected loaderShow(): void {

    this.loadingPanelVisible.set(true);
  }

  /** Hides the full-page loading overlay. */
  protected loaderClose(): void {

    this.loadingPanelVisible.set(false);
  }

  /** Builds a fallback file name of the form Users-<Company>-yyyyMMdd.xlsx */
  protected buildDownloadFileName(): string {

    const company = (this.companyName ?? 'Users')
      .replace(/[^\w\- ]+/g, '')
      .trim()
      .replace(/\s+/g, '-');

    const now = new Date();
    const stamp = now.getFullYear().toString()
      + String(now.getMonth() + 1).padStart(2, '0')
      + String(now.getDate()).padStart(2, '0');

    return `Users-${company}-${stamp}${this.ctDownloadExtension}`;
  }

  /** Triggers a browser save of the supplied blob under the given file name. */
  protected saveBlob(blob: Blob, fileName: string): void {

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Extracts the file name from a Content-Disposition header.
   * Handles both filename="x" and filename*=UTF-8''x forms. Returns null when absent.
   */
  protected getFileNameFromHeader(header: string | null): string | null {

    if (!header)
      return null;

    const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (utf8?.[1])
      return decodeURIComponent(utf8[1].trim());

    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    if (plain?.[1])
      return plain[1].trim();

    return null;
  }

  /** Requests the Excel workbook from the server and saves it under the server-supplied name. */
  protected downloadUsers(req: RequestUserState): void {

    this.loaderShow();

    this.adminService.downloadUserStateExcel$(req)
      .pipe(
        takeUntil(this.destroy$)
        , finalize(() => this.loaderClose())
      )
      .subscribe({
        next: (resp) => {

          const blob = resp.body;

          if (!blob || blob.size === 0) {
            this.dialogNotification.openNotificationDialog(
              this.ctDownloadDialogTitle,
              'No users were available to download'
            );
            return;
          }

          const fileName = this.getFileNameFromHeader(resp.headers.get('Content-Disposition'))
            ?? this.buildDownloadFileName();

          this.saveBlob(blob, fileName);
        },
        error: () => {
          this.dialogNotification.openNotificationDialog(
            this.ctDownloadDialogTitle,
            'The user download failed. You may not have access to this company\'s users.'
          );
        }
      });
  }

}
